import React, { useEffect, useMemo, useRef } from 'react'

import { useStyles } from './FlyOutArnDetails.styles'
import { CFSButtonV2, TextInputV4 } from '@labrav/react-components'
import {
  Sections,
  TestConnectionStatusProps,
  TestConnectionStatusType,
} from '../../../modules/flyOutOnboarding/reducer'
import {
  flyOutCFTLink,
  flyOutCFTLinkNotOwnS3Bucket,
  flyoutFormDebounceTime,
} from '../../../common/utils/constants'
import { FormikProps, useFormikContext } from 'formik'
import { useAccountId } from '@labrav/common'
import { flyOutTestConnection } from '../../../modules/flyOutOnboarding/action'
import { useDispatch } from '../../../store'

import { Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLoader } from '@fortawesome/pro-duotone-svg-icons'
import { faCircleCheck } from '@fortawesome/free-regular-svg-icons'
import { clsx } from 'clsx'
import { FormLockedType } from '../../../types/FormLockedType'
import { faCircleXmark } from '@fortawesome/pro-regular-svg-icons'
import { useFlagValue } from '@labrav/flags'

type FlyOutArnDetailsProps = FormLockedType & {
  showConnectionStatus?: boolean
}

export const FlyOutArnDetails: React.FC<FlyOutArnDetailsProps> = ({
  isFormLocked,
  showConnectionStatus = true,
}) => {
  const {
    values,
    errors,
    touched,
    status,
    handleBlur,
    handleChange,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext() as FormikProps<Sections>
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const accountId = useAccountId({ product: 'flyout' })
  const cloudSettings = values.cloudSettings
  const settingsTouched = touched?.cloudSettings
  const settingsError = errors?.cloudSettings
  const isOwnS3Bucket = values.cloudSettings.isOwnS3Bucket === true
  const failedConnectionField = (
    status as {
      failedConnectionField?: 'cloudRoleArn' | 'cloudCasS3BucketName'
    }
  )?.failedConnectionField

  const { value: isMarketplaceCloudSettingsRevampEnabled } = useFlagValue(
    'marketplaceCloudSettingsRevamp'
  )
  const { value: isOnboardingImprovementsEnabled } = useFlagValue(
    'onboardingImprovements'
  )
  const isTerraformStack =
    Boolean(isOnboardingImprovementsEnabled) &&
    values.cloudSettings.stackType === 'terraform'
  const testConnectionStatus = values.cloudSettings.testConnectionStatus
  const testConnectionDebounceRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null)
  useEffect(() => {
    const nextConnectionTested = testConnectionStatus === 'success'
    const currentStatus = values.cloudSettings.testConnectionStatus ?? ''
    const nextStatus = testConnectionStatus ?? ''
    if (
      values.cloudSettings.connectionTested === nextConnectionTested &&
      currentStatus === nextStatus
    ) {
      return
    }
    void (async () => {
      await setFieldValue(
        'cloudSettings.connectionTested',
        nextConnectionTested
      )
      await setFieldValue(
        'cloudSettings.testConnectionStatus',
        testConnectionStatus
      )
    })()
  }, [
    testConnectionStatus,
    values.cloudSettings.connectionTested,
    values.cloudSettings.testConnectionStatus,
    setFieldValue,
  ])

  const handleChangeField = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    await handleChange(event)
    if (event?.target?.name === 'cloudSettings.cloudRoleArn') {
      await updateFlyoutTestConnection({ status: 'standBy' })
      if (isTerraformStack) {
        await setFieldValue('cloudSettings.terraformConnectionValidated', false)
      }
      if (testConnectionDebounceRef.current) {
        clearTimeout(testConnectionDebounceRef.current)
      }
      const arn = event.target.value || ''
      if (arn) {
        await setFieldTouched('cloudSettings.cloudRoleArn', true, true)
      }
      if (!arn) {
        return
      }
      if (isMarketplaceCloudSettingsRevampEnabled) {
        if (!isOwnS3Bucket) {
          return
        }
      }
      testConnectionDebounceRef.current = setTimeout(() => {
        void dispatch(
          flyOutTestConnection(
            accountId || '',
            arn,
            isMarketplaceCloudSettingsRevampEnabled,
            updateFlyoutTestConnection
          )
        )
      }, flyoutFormDebounceTime)
    }
    await setFieldValue('cloudSettings.formFilled', false)
    await setFieldValue('cloudSettings.formUpdated', true)
  }
  const handleBlurRoleArn = async (
    event: React.FocusEvent<HTMLInputElement>
  ) => {
    await handleBlur(event)
  }

  useEffect(() => {
    return () => {
      if (testConnectionDebounceRef.current) {
        clearTimeout(testConnectionDebounceRef.current)
      }
    }
  }, [])

  const updateFlyoutTestConnection = async (
    testConnectionStatus: TestConnectionStatusProps
  ) => {
    await setFieldValue(
      'cloudSettings.testConnectionStatus',
      testConnectionStatus.status
    )
  }
  const cftLaunchLink = useMemo(() => {

  const base = isOwnS3Bucket ? flyOutCFTLink : flyOutCFTLinkNotOwnS3Bucket
    const externalId = String(accountId)
    const PartnerOrgName =
      String(cloudSettings.cloudAccountName) ||
      String(values.labraPartnerDetails?.companyName)
    const modifiedPartnerOrgname = PartnerOrgName.toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
    const link = `${base}&param_ExternalId=${externalId}&param_PartnerOrgName=${modifiedPartnerOrgname}`
    return link
  }, [
    isOwnS3Bucket,
    cloudSettings.cloudAccountId,
    cloudSettings.cloudAccountName,
    accountId,
    values.labraPartnerDetails?.companyName,
  ])

  const arnValidationMessage =
    settingsTouched?.cloudRoleArn && settingsError?.cloudRoleArn
      ? String(settingsError.cloudRoleArn)
      : ''

  const isRoleArnFilled = Boolean(String(cloudSettings.cloudRoleArn ?? ''))
  let cftLink
  if (isMarketplaceCloudSettingsRevampEnabled) {
    cftLink = cftLaunchLink
  } else {
    cftLink = flyOutCFTLink
  }

  return (
    <div className={classes.fieldsArnContainer}>
      {isTerraformStack ? '' : <CFSButtonV2 cfLink={cftLink} />}
      <div className={classes.arnInputAndStatus}>
        <div className={classes.fieldsContainer}>
          <div className={clsx(classes.field)}>
            <TextInputV4
              keyName="cloudSettings.cloudRoleArn"
              title="Enter your platform role ARN"
              type="text"
              placeHolder=""
              value={cloudSettings.cloudRoleArn}
              isError={
                !!(
                  settingsError?.cloudRoleArn && settingsTouched?.cloudRoleArn
                ) ||
                (testConnectionStatus === 'failure' &&
                  (isOwnS3Bucket || failedConnectionField === 'cloudRoleArn'))
              }
              onChange={handleChangeField}
              onBlur={handleBlurRoleArn}
              isReadOnly={isFormLocked as boolean}
              isRequired={false}
              errorText=""
              showSpinner={true}
            />
            {arnValidationMessage ? (
              <div className={classes.arnHelperRow}>
                <Typography className={classes.errorText}>
                  {arnValidationMessage}
                </Typography>
              </div>
            ) : !isRoleArnFilled ? (
              <div className={classes.arnHelperRow}>
                <Typography className={classes.arnHintText}>
                  E.g. arn:aws:iam::023678306288:role/flyout-role-labra
                </Typography>
              </div>
            ) : null}
          </div>
        </div>
        {showConnectionStatus ? (
          <div className={classes.field} data-testid="test-connection">
            <ShowConnectionStatuses
              testConnectionStatus={{ status: testConnectionStatus }}
            />
          </div>
        ) : null}
      </div>
    </div>
  )
}

export interface ShowConnectionStatusesProps {
  testConnectionStatus: {
    status: TestConnectionStatusType
    error?: string
  }
  onRetry?: () => void
  successMessage?: string
  failureMessage?: string
}
export const ShowConnectionStatuses = React.memo(
  ({
    testConnectionStatus,
    onRetry,
    successMessage = 'Connection established successfully',
    failureMessage = 'Test connection failed',
  }: ShowConnectionStatusesProps) => {
    const { classes } = useStyles()
    switch (testConnectionStatus.status) {
      case 'loading': {
        return (
          <div
            className={classes.loadingTextContainer}
            data-testid="loading-test-connection"
          >
            <FontAwesomeIcon icon={faLoader} spin className={classes.loader} />
            <Typography
              className={clsx(classes.loadingText, classes.textColor)}
            >
              Testing connection, please wait...
            </Typography>
          </div>
        )
      }
      case 'failure': {
        return (
          <>
            <div
              className={classes.loadingTextContainer}
              data-testid="failed-test-connection"
            >
              <FontAwesomeIcon icon={faCircleXmark} className={classes.cross} />
              <Typography
                className={clsx(
                  classes.loadingText,
                  classes.errorText,
                  classes.tryAgain
                )}
              >
                {testConnectionStatus.error || failureMessage}
                <div className={classes.verticalDevider}></div>
                {!testConnectionStatus.error && onRetry && (
                  <span
                    onClick={onRetry}
                    className={classes.tryAgainText}
                    data-testid="retry-connection"
                  >
                    Try again
                  </span>
                )}
              </Typography>
            </div>
          </>
        )
      }
      case 'success': {
        return (
          <div
            className={classes.loadingTextContainer}
            data-testid="success-test-connection"
          >
            <FontAwesomeIcon
              icon={faCircleCheck}
              className={clsx(classes.loader)}
            />
            <Typography
              className={clsx(classes.successText, classes.textColor)}
            >
              {successMessage}
            </Typography>
          </div>
        )
      }
      default:
        return <div data-testid="empty-connection-string"></div>
    }
  }
)
